// shared/src/schemas/shadow-arbiter-signal.ts
// Zod guard + row types for the p.arbiter Pro-vs-Flash shadow A/B tables
// (DDL: 0064_shadow_arbiter_signals.sql). The Flash "challenger" arbiter output
// is kept in shadow_arbiter_signals — entirely separate from the live `signals`
// table so shadow predictions can NEVER leak into the product. Field names
// mirror the DDL 1:1 (schema-vs-ddl gate).
//
// SINGLE source of truth (audit 2026-06-22-001 -003). The WRITER was RETIRED
// 2026-08-06 (cost — the Pro challenger call was removed); this schema is now
// READ-ONLY over the already-collected rows —
//   · scorer: workers/daily-close-scorer/src/shadow-score.ts (reads a narrow subset)
//   · dashboard: api-worker/lib/arbiter-ab-aggregator.ts + apps/web arbiter-ab
// The rows keep grading (free) so the 60/90d verdict still matures on existing data.
// Design: docs/p-arbiter-shadow-ab-2026-06-20-brief-revision.md

import { z } from 'zod';

export const ShadowArbiterDirectionEnum     = z.enum(['long', 'short']);
export const ShadowArbiterConfidenceBucketEnum = z.enum(['HIGH', 'MED', 'LOW']);
export const ShadowArbiterPredictionModeEnum = z.enum(['catalyst', 'trend']);

// Full row (15 columns, mirrors shadow_arbiter_signals 1:1). Historically written
// by p-stage-worker; writer retired 2026-08-06 — schema now validates reads.
export const ShadowArbiterSignalSchema = z.object({
  id:                z.string(),                          // UUID
  brief_id:          z.string(),
  ticker:            z.string(),
  model:             z.string(),                          // challenger model, e.g. 'deepseek-v4-flash'
  signal_at:         z.string(),                          // ISO-Z; equals the live signal's signal_at
  signal_date:       z.string(),                          // ET trading date used by the scorer
  direction:         ShadowArbiterDirectionEnum,
  magnitude:         z.number(),
  confidence:        z.number().min(0).max(1),
  confidence_bucket: ShadowArbiterConfidenceBucketEnum,
  signal_price:      z.number().positive(),
  event_horizon_at:  z.string(),                          // ISO-Z; the Flash arbiter's own horizon
  prediction_mode:   ShadowArbiterPredictionModeEnum,
  created_at:        z.string(),
  details:           z.string(),                          // JSON sidecar
});

export type ShadowArbiterSignalRow = z.infer<typeof ShadowArbiterSignalSchema>;

// Narrow read-subset the scorer (daily-close-scorer/shadow-score.ts) selects to
// grade outcomes — keyed Pick of the full row so it can never drift from it.
export type ShadowArbiterSignalScoreRow = Pick<
  ShadowArbiterSignalRow,
  'id' | 'ticker' | 'signal_at' | 'signal_price' | 'direction'
>;

// Graded outcome row (shadow_arbiter_signal_outcomes), mirrors the DDL 1:1.
export const ShadowArbiterOutcomeEnum = z.enum(['correct', 'incorrect', 'neutral']);
export const ShadowArbiterCloseSourceEnum = z.enum(['daily_closes', 'intraday_fallback', 'manual_correction']);

export const ShadowArbiterSignalOutcomeSchema = z.object({
  id:             z.string(),
  shadow_id:      z.string(),
  horizon:        z.enum(['1d', '5d', '10d', '20d', '60d', '90d']),
  realized_price: z.number().positive(),
  outcome:        ShadowArbiterOutcomeEnum,
  realized_at:    z.string(),
  scored_at:      z.string(),
  scorer_version: z.string(),
  close_source:   ShadowArbiterCloseSourceEnum,
});

export type ShadowArbiterSignalOutcomeRow = z.infer<typeof ShadowArbiterSignalOutcomeSchema>;
